import { trim } from "lodash";
import { Box, Flex, FlexItem } from "orcs-design-system";
import PropTypes from "prop-types";
import React, { useState, useEffect, useCallback } from "react";

import ErrorNotification from "src/components/ErrorNotification";
import {
  validateTeamName,
  sanitizeTeamName,
} from "src/util/teamNameValidation";
import ConfirmAndCancelBtns from "./ConfirmAndCancelBtns";
import EditBtn from "./EditBtn";
import TeamName from "./TeamName";
import TeamNameInput from "./TeamNameInput";

const TeamNameEditor = ({
  team,
  updateName,
  label,
  isEditMode = false,
  canCancel = true,
  autoExitEditMode = true,
}) => {
  const { name } = team;

  const [editMode, setEditMode] = useState(isEditMode);
  const [isLoading, setLoading] = useState(false);
  const [newName, setNewName] = useState(name);
  const [error, setError] = useState(null);

  useEffect(() => {
    setNewName(name);
  }, [name]);

  const onEdit = useCallback(() => {
    setEditMode(true);
  }, []);

  const onSave = useCallback(async () => {
    // Validate the original input first (before sanitization)
    const validationResult = validateTeamName(newName);
    if (validationResult) {
      setError(validationResult);
      return;
    }

    // Clear any previous errors since validation passed
    setError(null);

    // Sanitize before saving to ensure clean data is sent to backend
    const sanitizedName = sanitizeTeamName(newName);
    const proposedName = trim(sanitizedName);

    if (proposedName === name) {
      return;
    }

    try {
      setLoading(true);
      await updateName(proposedName);
      setError(null);
    } catch (err) {
      // Extract clean error message from Apollo error structure without prefix
      const errorMessage = err.graphQLErrors?.[0]?.message || err.toString();
      setError(errorMessage);
    } finally {
      setLoading(false);
      if (autoExitEditMode) {
        setEditMode(false);
      }
    }
  }, [newName, name, updateName, autoExitEditMode]);

  const onCancel = useCallback(() => {
    setNewName(name);
    setEditMode(false);
    setError(null);
  }, [name]);

  const onNameChange = useCallback((e) => {
    setNewName(e.target.value);
  }, []);

  const onNameBlur = useCallback(
    (e) => {
      const inputValue = e.target.value;

      // Validate on blur if name has changed
      if (inputValue !== name) {
        const result = validateTeamName(inputValue);
        if (result) {
          setError(result);
        } else {
          setError(null);
        }
      } else {
        setError(null);
      }
    },
    [name]
  );

  return (
    <Box>
      <Flex alignItems="center" mb="r" flexWrap="wrap">
        <TeamName isEditMode={editMode} name={name} />
        <FlexItem flex="1 1 270px">
          <TeamNameInput
            isEditMode={editMode}
            isLoading={isLoading}
            value={newName}
            onChange={onNameChange}
            onBlur={onNameBlur}
            onSave={onSave}
            onCancel={onCancel}
            label={label}
          />
        </FlexItem>
        <EditBtn isEditMode={editMode} onEdit={onEdit} />
        <ConfirmAndCancelBtns
          isEditMode={editMode}
          isLoading={isLoading}
          canCancel={canCancel}
          saveDisabled={newName === name}
          onSave={onSave}
          onCancel={onCancel}
        />
      </Flex>
      {error && (
        <ErrorNotification message={error} error={error} report={false} />
      )}
    </Box>
  );
};

TeamNameEditor.propTypes = {
  team: PropTypes.object.isRequired,
  label: PropTypes.string,
  updateName: PropTypes.func,
  isEditMode: PropTypes.bool,
  canCancel: PropTypes.bool,
  autoExitEditMode: PropTypes.bool,
};

export default TeamNameEditor;
